"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
	type ReactNode,
	createContext,
	use,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useSession } from "@/lib/auth-client";
import {
	mapBetterAuthUserToUser,
	mergeMembershipFromApi,
	useUserMembership,
	useUserPermissions,
	useUserSecurityFlags,
	userMembershipQueryKey,
} from "@/lib/auth/user-auth-queries";
import { syncPosthogIdentity } from "@/lib/posthog-browser";
import type { MembershipPlanId, User } from "@/types/membership";

interface AuthContextValue {
	user: User | null;
	isLoggedIn: boolean;
	/** Debug / local override only — merges into derived `user.membership`, does not mirror a full User. */
	setMembershipPlan: (planId: MembershipPlanId) => void;
	/**
	 * True only while the Better Auth session is resolving (`useSession` pending).
	 * Do not use membership/security queries here: they can hang and must not block
	 * session-gated UI (e.g. account after back from Stripe / bfcache).
	 */
	isSessionResolving: boolean;
	/**
	 * Superset: session or initial membership/security fetch for the current user.
	 * Prefer `isSessionResolving` for “can we read login state?”.
	 */
	isLoading: boolean;
	/** Refetch membership from API (e.g. after checkout) */
	refetchMembership: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
	const { data: session, isPending, refetch: refetchSession } = useSession();
	const queryClient = useQueryClient();
	const [debugMembershipPlan, setDebugMembershipPlan] = useState<MembershipPlanId | null>(null);

	const sessionUserId = session?.user?.id ?? null;

	const { data: userPermissions } = useUserPermissions(sessionUserId);
	const {
		data: membershipData,
		isPending: membershipPending,
		refetch: refetchMembershipQuery,
	} = useUserMembership(sessionUserId);
	const { data: securityFlags, isPending: securityFlagsPending } =
		useUserSecurityFlags(sessionUserId);

	const refetchMembership = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: userMembershipQueryKey(sessionUserId ?? "_"),
		});
		await refetchMembershipQuery();
	}, [queryClient, sessionUserId, refetchMembershipQuery]);

	/**
	 * `pageshow` with `persisted` == browser restored this document from the **back-forward cache**
	 * (bfcache): it does not re-run a full load, so client auth state can be wrong until you
	 * hard-refresh. Session refetch is not always enough; a full reload fixes it. See:
	 * https://web.dev/bfcache/
	 */
	const lastSessionResyncAt = useRef(0);
	const previousUserIdRef = useRef<string | undefined>(undefined);
	useEffect(() => {
		const currentUserId = session?.user?.id;
		if (previousUserIdRef.current && !currentUserId) {
			setDebugMembershipPlan(null);
			void queryClient.invalidateQueries({ queryKey: ["pools"] });
			queryClient.removeQueries({ queryKey: ["favorites"] });
		}
		previousUserIdRef.current = currentUserId;
	}, [session?.user?.id, queryClient]);

	useEffect(() => {
		const resync = () => {
			const now = Date.now();
			if (now - lastSessionResyncAt.current < 400) return;
			lastSessionResyncAt.current = now;
			void refetchSession();
		};

		const onPageShow = (event: PageTransitionEvent) => {
			// Initial navigation also fires `pageshow` — only resync after bfcache restore.
			if (event.persisted) resync();
		};
		window.addEventListener("pageshow", onPageShow);
		/* Hidden → visible (bfcache / tab) */
		const onVis = () => {
			if (document.visibilityState === "visible") resync();
		};
		document.addEventListener("visibilitychange", onVis);
		return () => {
			window.removeEventListener("pageshow", onPageShow);
			document.removeEventListener("visibilitychange", onVis);
		};
	}, [refetchSession]);

	/** Merge anonymous browser events into authenticated person on any session resolve. */
	useEffect(() => {
		const userId = session?.user?.id ?? null;
		void syncPosthogIdentity(userId);
	}, [session?.user?.id]);

	const user = useMemo((): User | null => {
		if (!session?.user) return null;
		const createdAt = new Date(session.user.createdAt || Date.now());
		const membership = mergeMembershipFromApi(
			debugMembershipPlan,
			membershipData ?? null,
			createdAt,
		);
		return mapBetterAuthUserToUser(
			session.user,
			userPermissions?.role,
			userPermissions?.permissions,
			membership,
			securityFlags,
		);
	}, [
		session?.user,
		userPermissions?.role,
		userPermissions?.permissions,
		debugMembershipPlan,
		membershipData,
		securityFlags,
	]);

	const setMembershipPlan = useCallback((planId: MembershipPlanId) => {
		setDebugMembershipPlan(planId);
	}, []);

	const isSessionResolving = isPending;
	const isLoading =
		isPending ||
		(!!sessionUserId && membershipPending && !debugMembershipPlan) ||
		(!!sessionUserId && securityFlagsPending);

	const value = useMemo(
		() => ({
			user,
			isLoggedIn: user !== null,
			setMembershipPlan,
			isSessionResolving,
			isLoading,
			refetchMembership,
		}),
		[user, setMembershipPlan, isSessionResolving, isLoading, refetchMembership],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = use(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
